import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, ErrorState, HeaderRow, LoadingState, Pager, TableShell, TH } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiErrorMessage } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { BooksScreen, PostingStatusBadge, SOURCE_LABEL, sourceHref } from "./shared";
import type { Book, Posting, PostingStatus } from "./types";

const TABS: Array<{ status: PostingStatus; label: string; empty: string }> = [
  { status: "Failed", label: "Failed", empty: "Nothing failed to post." },
  { status: "Waiting", label: "Waiting", empty: "Nothing is waiting on a missing setting." },
  { status: "Skipped", label: "Skipped", empty: "Nothing was skipped." },
];

export function PostingIssues() {
  return (
    <BooksScreen title="Posting issues" subtitle="Documents that are not in the books, and why.">
      {(book) => <PostingIssuesBody book={book} />}
    </BooksScreen>
  );
}

function PostingIssuesBody({ book }: { book: Book }) {
  const { books, canManage, links } = useBooks();
  const [params, setParams] = useSearchParams();
  const status = (TABS.find((t) => t.status === params.get("status"))?.status ?? "Failed") as PostingStatus;
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [retrying, setRetrying] = useState<string | null>(null);
  const cursor = cursors[cursors.length - 1];

  const page = useLoad(
    () => books.GET("/v1/books/current/postings", { params: { query: { Status: status, Cursor: cursor, Limit: 50 } } }),
    [books, status, cursor],
  );
  const rows = page.data?.data ?? [];

  async function retry(p: Posting) {
    setRetrying(p.id!);
    const { error } = await books.POST("/v1/books/current/postings/{id}/retry", { params: { path: { id: p.id! } } });
    setRetrying(null);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't retry the posting."));
      return;
    }
    toast.success("Posting again.");
    await page.reload();
  }

  return (
    <>
      <p className="mb-3 text-sm text-muted-foreground">
        Issues show for every period: a document that did not post needs fixing whatever its date.
      </p>
      <Tabs
        value={status}
        onValueChange={(v) => {
          setCursors([undefined]);
          setParams({ status: v }, { replace: true });
        }}
        className="mb-4"
      >
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.status} value={t.status}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {page.loading && !page.data ? (
        <LoadingState label="Loading postings" />
      ) : page.error ? (
        <ErrorState message={apiErrorMessage(page.error, "Couldn't load the postings.")} onRetry={page.reload} />
      ) : rows.length === 0 ? (
        <EmptyState icon={CheckCircle2} title="All clear">
          {TABS.find((t) => t.status === status)!.empty}
        </EmptyState>
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Document</TableHead>
                  <TableHead className={TH}>Status</TableHead>
                  <TableHead className={TH}>Reason</TableHead>
                  <TableHead className={cn(TH, "hidden text-right @3xl:table-cell")}>Attempts</TableHead>
                  <TableHead className={cn(TH, "hidden @3xl:table-cell")}>Last tried</TableHead>
                  {canManage && book.status === "Active" ? <TableHead className={TH} /> : null}
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => {
                  const href = sourceHref(links, p.sourceType, p.sourceId);
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">
                        {href ? (
                          <Link to={href} className="underline-offset-4 hover:underline">
                            {SOURCE_LABEL[p.sourceType!]}
                          </Link>
                        ) : (
                          SOURCE_LABEL[p.sourceType!]
                        )}
                      </TableCell>
                      <TableCell>
                        <PostingStatusBadge posting={{ status: p.status }} />
                      </TableCell>
                      <TableCell className="max-w-md text-sm text-muted-foreground">
                        {p.reason || "-"}
                        {/* In a narrow pane (chat open) the attempts fold under the reason. */}
                        <div className="mt-0.5 text-xs tabular-nums @3xl:hidden">
                          {p.attempts} {p.attempts === 1 ? "attempt" : "attempts"}
                          {p.lastAttemptAt ? `, last ${formatDateTime(p.lastAttemptAt)}` : ""}
                        </div>
                      </TableCell>
                      <TableCell className="hidden text-right tabular-nums @3xl:table-cell">{p.attempts}</TableCell>
                      <TableCell className="hidden whitespace-nowrap text-muted-foreground @3xl:table-cell">
                        {p.lastAttemptAt ? formatDateTime(p.lastAttemptAt) : "-"}
                      </TableCell>
                      {canManage && book.status === "Active" ? (
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" disabled={retrying !== null} onClick={() => retry(p)}>
                            {retrying === p.id ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />} Retry
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>
          <Pager
            page={cursors.length}
            hasPrevious={cursors.length > 1}
            hasNext={Boolean(page.data?.meta?.cursors?.next)}
            disabled={page.loading}
            onPrevious={() => setCursors((c) => c.slice(0, -1))}
            onNext={() => setCursors((c) => [...c, page.data?.meta?.cursors?.next ?? undefined])}
          />
        </>
      )}
    </>
  );
}
